import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from './api';
import FileTypeIcon, { isPreviewable } from './FileTypeIcon';
import FilePreview from './FilePreview';
import RichTextEditor from './RichTextEditor';
import sanitizeHtml from './sanitizeHtml';
import { formatRelativeDate } from './dateUtils';
import './PostCard.css';
import './FileUpload.css';

export default function PostCard({ post, index = 0, onPostUpdate, user }) {
  const [expanded, setExpanded] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const bodyRef = useRef(null);
  // Текст обрезается по высоте средствами CSS, поэтому «Читать далее» и
  // затухание внизу показываем только если он действительно не поместился.
  // Считать по числу символов нельзя: короткий текст со списком бывает выше
  // ограничения, а длинный в одну строку — ниже.
  const [isClamped, setIsClamped] = useState(false);
  const [poll, setPoll] = useState(post.poll);
  const [voting, setVoting] = useState(false);
  const [expandedOption, setExpandedOption] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sections, setSections] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    section: '',
    subcategory: '',
    pinned: false,
    is_public: true,
    is_important: false,
    created_at: '',
  });
  const [pollData, setPollData] = useState({
    hasPoll: false,
    question: '',
    multiple: false,
    options: ['', ''],
  });
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [attachmentDisplayNames, setAttachmentDisplayNames] = useState({});
  const [deleting, setDeleting] = useState(false);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => {
      // В развёрнутом виде ограничения нет, мерить нечего:
      // прежнее значение сохраняем, чтобы кнопка «Свернуть» не исчезла.
      if (expanded) return;
      setIsClamped(el.scrollHeight > el.clientHeight + 1);
    };
    measure();

    // Одного замера при монтировании мало: высота текста меняется от ширины
    // колонки, дозагрузки шрифтов и появления полосы прокрутки. ResizeObserver
    // ловит все эти случаи, обычное событие resize — только часть.
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [post.content, expanded]);

  useEffect(() => {
    setPoll(post.poll);
  }, [post.poll]);

  useEffect(() => {
    // Блокируем прокрутку страницы при открытии модального окна
    if (showEditModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Очистка при размонтировании
    return () => {
      document.body.style.overflow = '';
    };
  }, [showEditModal]);

  const isAdmin = user && (user.role === 'admin' || user.is_admin || user.is_superuser);
  
  const formatDate = (dateString) => formatRelativeDate(dateString);

  // Функция для распознавания и преобразования URL в кликабельные ссылки
  // Адреса ищем по текстовым узлам разобранного HTML, а не по строке.
  // Прежняя версия сравнивала число открывающих и закрывающих тегов перед
  // адресом и пропускала его, если они не совпали, — внутри <ul><li> или
  // после <br> баланс не сходится никогда, и ссылки в списках оставались
  // обычным текстом.
  const linkifyContent = (html) => {
    if (!html) return '';

    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/[^\s<>"{}|\\^`\[\]]*)?)/gi;

    const template = document.createElement('template');
    template.innerHTML = html;

    // Внутри уже существующих ссылок и блоков кода ничего не трогаем
    const SKIP_TAGS = new Set(['A', 'CODE', 'PRE']);
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      let parent = node.parentNode;
      let skip = false;
      while (parent && parent.nodeType === Node.ELEMENT_NODE) {
        if (SKIP_TAGS.has(parent.tagName)) { skip = true; break; }
        parent = parent.parentNode;
      }
      if (!skip) textNodes.push(node);
    }

    textNodes.forEach((textNode) => {
      const text = textNode.textContent;
      urlRegex.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let match;

      while ((match = urlRegex.exec(text)) !== null) {
        let raw = match[0];
        // Точка или скобка в конце — это знак препинания предложения,
        // а не часть адреса
        const trailing = raw.match(/[.,;:!?)\]]+$/);
        if (trailing) raw = raw.slice(0, raw.length - trailing[0].length);
        if (!raw) continue;

        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }

        const link = document.createElement('a');
        link.className = 'auto-link';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        link.textContent = raw;
        fragment.appendChild(link);

        lastIndex = match.index + raw.length;
      }

      if (lastIndex === 0) return;
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      textNode.replaceWith(fragment);
    });

    return template.innerHTML;
  };

  const handleVote = async (optionId) => {
    if (voting || !poll) return;
    
    try {
      setVoting(true);
      const updatedPoll = await api.votePoll(optionId);
      setPoll(updatedPoll);
      
      // Обновляем пост в родительском компоненте
      if (onPostUpdate) {
        const updatedPost = { ...post, poll: updatedPoll };
        onPostUpdate(updatedPost);
      }
    } catch (err) {
      console.error('Error voting:', err);
      alert(err.message || 'Ошибка при голосовании');
    } finally {
      setVoting(false);
    }
  };

  const handleEditClick = async () => {
    try {
      setLoading(true);
      // Загружаем разделы и подкатегории
      const [sectionsData, subcategoriesData] = await Promise.all([
        api.getSections(),
        api.getSubcategories(),
      ]);
      setSections(sectionsData);
      setSubcategories(subcategoriesData);
      
      // Заполняем форму данными поста
      const postDate = post.created_at ? new Date(post.created_at) : new Date();
      const formattedDate = postDate.toISOString().slice(0, 16); // Формат для datetime-local
      
      setFormData({
        title: post.title || '',
        content: post.content || '',
        section: post.section?.toString() || '',
        subcategory: post.subcategory?.toString() || '',
        pinned: post.pinned || false,
        is_public: post.is_public !== false,
        is_important: post.is_important || false,
        created_at: formattedDate,
      });
      
      if (post.poll) {
        setPollData({
          hasPoll: true,
          question: post.poll.question || '',
          multiple: post.poll.multiple || false,
          options: post.poll.options?.map(opt => opt.text) || ['', ''],
        });
      } else {
        setPollData({
          hasPoll: false,
          question: '',
          multiple: false,
          options: ['', ''],
        });
      }
      
      setAttachments([]);
      // Загружаем существующие файлы поста
      setExistingAttachments(post.attachments || []);
      // Загружаем display_name для существующих файлов
      const displayNames = {};
      (post.attachments || []).forEach(att => {
        if (att.id) {
          displayNames[att.id] = att.display_name || '';
        }
      });
      setAttachmentDisplayNames(displayNames);
      setShowEditModal(true);
      setError(null);
    } catch (err) {
      setError(err.message || 'Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить этот пост? Пост будет перемещен в корзину.')) {
      return;
    }
    
    try {
      setDeleting(true);
      await api.deletePost(post.id);
      
      // Уведомляем родительский компонент об удалении
      if (onPostUpdate) {
        onPostUpdate(null); // null означает удаление
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      
      // Показываем более понятное сообщение об ошибке
      let errorMessage = err.message || 'Ошибка при удалении поста';
      
      // Если ошибка связана с миграцией, показываем специальное сообщение
      if (errorMessage.includes('миграцию') || errorMessage.includes('migration') || 
          errorMessage.includes('deleted_at') || errorMessage.includes('недоступно')) {
        errorMessage = 'Мягкое удаление недоступно. Необходимо выполнить миграцию базы данных.\n\n' +
                      'Выполните в терминале:\n' +
                      'cd backend\n' +
                      'python manage.py migrate';
      }
      
      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePollChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPollData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePollOptionChange = (index, value) => {
    setPollData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  };

  const addPollOption = () => {
    setPollData(prev => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  };

  const removePollOption = (index) => {
    setPollData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = (attachmentId) => {
    setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
    // Удаляем display_name для удаленного файла
    setAttachmentDisplayNames(prev => {
      const newNames = { ...prev };
      delete newNames[attachmentId];
      return newNames;
    });
  };

  const updateAttachmentDisplayName = (attachmentId, displayName) => {
    setAttachmentDisplayNames(prev => ({
      ...prev,
      [attachmentId]: displayName
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content);
      if (formData.section) formDataToSend.append('section', formData.section);
      if (formData.subcategory) formDataToSend.append('subcategory', formData.subcategory);
      // FormData приводит значение к строке: undefined превратилось бы
      // в "undefined", и сервер отклонил бы поле как не-булево.
      formDataToSend.append('pinned', formData.pinned ? 'true' : 'false');
      formDataToSend.append('is_public', formData.is_public ? 'true' : 'false');
      formDataToSend.append('is_important', formData.is_important ? 'true' : 'false');
      if (formData.created_at) {
        // Конвертируем datetime-local в ISO формат для Django
        const dateObj = new Date(formData.created_at);
        formDataToSend.append('created_at', dateObj.toISOString());
      }

      // Добавляем новые файлы
      attachments.forEach((file, index) => {
        formDataToSend.append(`attachment_${index}`, file);
        // Добавляем display_name для нового файла, если указан
        const displayName = attachmentDisplayNames[`new_${index}`] || '';
        if (displayName) {
          formDataToSend.append(`attachment_display_name_${index}`, displayName);
        }
      });

      // Добавляем список удаленных файлов
      const allExistingIds = (post.attachments || []).map(att => att.id);
      const remainingIds = existingAttachments.map(att => att.id);
      const deletedIds = allExistingIds.filter(id => !remainingIds.includes(id));
      if (deletedIds.length > 0) {
        formDataToSend.append('delete_attachments', JSON.stringify(deletedIds));
      }

      // Добавляем обновления display_name для существующих файлов
      const attachmentUpdates = {};
      existingAttachments.forEach(att => {
        if (att.id && attachmentDisplayNames[att.id] !== undefined) {
          const currentDisplayName = attachmentDisplayNames[att.id] || '';
          const originalDisplayName = (post.attachments || []).find(a => a.id === att.id)?.display_name || '';
          if (currentDisplayName !== originalDisplayName) {
            attachmentUpdates[att.id] = { display_name: currentDisplayName };
          }
        }
      });
      if (Object.keys(attachmentUpdates).length > 0) {
        formDataToSend.append('attachment_updates', JSON.stringify(attachmentUpdates));
      }

      if (pollData.hasPoll && pollData.question && pollData.question.trim()) {
        formDataToSend.append('poll_question', pollData.question);
        formDataToSend.append('poll_multiple', pollData.multiple ? 'true' : 'false');
        const validOptions = pollData.options.filter(opt => opt && opt.trim());
        validOptions.forEach((option, index) => {
          formDataToSend.append(`poll_option_${index}`, option.trim());
        });
      } else if (pollData.hasPoll === false) {
        formDataToSend.append('poll_question', '');
      }
      
      const updatedPost = await api.updatePost(post.id, formDataToSend);
      
      // Обновляем пост в родительском компоненте
      if (onPostUpdate) {
        onPostUpdate(updatedPost);
      }
      
      setShowEditModal(false);
      setError(null);
    } catch (err) {
      setError(err.message || 'Ошибка при обновлении поста');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowEditModal(false);
    setError(null);
    setFormData({
      title: '',
      content: '',
      section: '',
      subcategory: '',
      pinned: false,
      is_public: true,
      is_important: false,
      created_at: '',
    });
    setPollData({
      hasPoll: false,
      question: '',
      multiple: false,
      options: ['', ''],
    });
    setAttachments([]);
    setExistingAttachments([]);
    setAttachmentDisplayNames({});
  };

  const filteredSubcategories = formData.section
    ? subcategories.filter(sub => sub.section === parseInt(formData.section))
    : [];

  return (
    <article 
      className={`post-card ${post.pinned ? 'pinned' : ''} ${post.is_important ? 'important' : ''}`}
      // Задержку приходится ограничивать: при index * 0.1 с карточки
      // в конце ленты оставались прозрачными по несколько секунд.
      style={{ animationDelay: `${Math.min(index, 9) * 0.06}s` }}
    >
      {post.pinned && (
        <div className="post-pin-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 17v5M9 10.76V2h6v8.76M9 10.76l-3 2.24M15 10.76l3 2.24M9 10.76V7h6v3.76M12 17H2v3c0 .55.45 1 1 1h18c.55 0 1-.45 1-1v-3h-10z"/>
          </svg>
          Закреплено
        </div>
      )}
      
      <div className="post-header">
        <div className="post-meta">
          <div className="post-author">
            {post.author_avatar && (
              <img src={post.author_avatar} alt={post.author_name} className="author-avatar" />
            )}
            <div className="author-info">
              <span className="author-name">{post.author_name || 'Администратор'}</span>
              <span className="post-date">{formatDate(post.created_at)}</span>
            </div>
          </div>
          <div className="post-header-actions">
            {post.is_important && (
              <div className="post-important-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="7.5" x2="12" y2="13"></line>
                  <line x1="12" y1="16.5" x2="12" y2="16.5"></line>
                </svg>
                Важно
              </div>
            )}
            {(post.section_title || post.subcategory_title) && (
              <div className="post-category-badges">
                {post.section_title && (
                  <div className="post-section-badge">
                    {post.section_title}
                  </div>
                )}
                {post.subcategory_title && (
                  <div className="post-subcategory-badge">
                    {post.subcategory_title}
                  </div>
                )}
              </div>
            )}
            {isAdmin && (
              <>
                <button
                  className="post-edit-button"
                  onClick={handleEditClick}
                  title="Редактировать пост"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
                <button
                  className="post-delete-button"
                  onClick={handleDelete}
                  disabled={deleting}
                  title="Удалить пост"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="post-content">
        <h3 className="post-title">{post.title}</h3>
        <div 
          ref={bodyRef}
          className={`post-body ${expanded ? 'expanded' : ''} ${isClamped ? 'clamped' : ''}`}
          dangerouslySetInnerHTML={{ 
            // Разметка выводится целиком в любом состоянии: свёрнутый вид
            // ограничивает высоту через CSS, поэтому списки и выделения
            // видны и в предпросмотре, а теги не рвутся по символам.
            __html: linkifyContent(sanitizeHtml(post.content))
          }}
        />
        {isClamped && (
          <button 
            className="expand-button"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Свернуть' : 'Читать далее'}
          </button>
        )}
      </div>

      {previewAttachment && createPortal(
        // Выносим окно в body: у карточки поста есть анимация с transform,
        // а такой предок становится точкой отсчёта для position: fixed —
        // без портала окно перестаёт быть полноэкранным.
        <FilePreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />,
        document.body
      )}

      {post.attachments && post.attachments.length > 0 && (
        <div className="post-attachments">
          <h4>Вложения:</h4>
          <div className="attachments-list">
            {post.attachments.map((attachment, idx) => (
              <a 
                key={idx}
                href={attachment.file_url || attachment.file}
                target="_blank"
                rel="noopener noreferrer"
                className="attachment-link"
                onClick={(e) => {
                  // Офисные документы браузер не показывает — открываем их
                  // в окне предпросмотра. Остальное (pdf, картинки)
                  // по-прежнему открывается в новой вкладке.
                  if (isPreviewable(attachment.file_name || attachment.file)) {
                    e.preventDefault();
                    setPreviewAttachment(attachment);
                  }
                }}
              >
                <FileTypeIcon
                  fileName={attachment.file_name || attachment.file || attachment.display_name}
                  fileType={attachment.file_type}
                  size={28}
                />
                <span className="attachment-name">
                  {attachment.display_name || attachment.file_name || 'Файл'}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {(poll || post.poll) && (
        <div className="post-poll">
          <h4>{(poll || post.poll).question}</h4>
          <div className="poll-options">
            {(poll || post.poll).options?.map((option, idx) => (
              <div key={option.id || idx} className="poll-option-wrapper">
                <button
                  className={`poll-option-button ${voting || !option.id ? 'disabled' : ''}`}
                  onClick={() => option.id && handleVote(option.id)}
                  disabled={voting || !option.id}
                >
                  <div className="poll-option-bar">
                    <div 
                      className="poll-option-fill"
                      style={{ width: `${option.percentage || 0}%` }}
                    />
                  </div>
                  <span className="poll-option-text">
                    {option.text} ({option.votes_count || 0})
                  </span>
                </button>
                {isAdmin && option.votes !== null && option.votes !== undefined && (
                  <div className="poll-votes-details">
                    {option.votes.length > 0 ? (
                      <>
                        <button
                          className="poll-votes-toggle"
                          onClick={() => setExpandedOption(expandedOption === option.id ? null : option.id)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {expandedOption === option.id ? (
                              <path d="M18 15l-6-6-6 6"/>
                            ) : (
                              <path d="M6 9l6 6 6-6"/>
                            )}
                          </svg>
                          Показать проголосовавших ({option.votes.length})
                        </button>
                        {expandedOption === option.id && (
                          <div className="poll-votes-list">
                            {option.votes.map((vote, voteIdx) => (
                              <div key={voteIdx} className="poll-vote-item">
                                <span className="poll-vote-user">
                                  {vote.user_full_name || vote.user_name}
                                </span>
                                <span className="poll-vote-time">
                                  {new Date(vote.voted_at).toLocaleString('ru-RU', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="poll-votes-empty">
                        <span style={{ fontSize: '12px', color: 'var(--eu-text-dim)' }}>Пока никто не проголосовал</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showEditModal && createPortal(
        <div className="post-edit-overlay" onClick={handleCancel}>
          <div className="post-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="post-edit-header">
              <h3>Редактировать пост</h3>
              <button className="post-edit-close" onClick={handleCancel}>×</button>
            </div>

            {error && (
              <div className="post-edit-error">{error}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="post-edit-form-group">
                <label>Заголовок *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="post-edit-form-group">
                <label>Содержание *</label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
                />
              </div>

              <div className="post-edit-form-row">
                <div className="post-edit-form-group">
                  <label>Раздел</label>
                  <select name="section" value={formData.section} onChange={handleInputChange}>
                    <option value="">Выберите раздел</option>
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>{section.title}</option>
                    ))}
                  </select>
                </div>

                <div className="post-edit-form-group">
                  <label>Подкатегория</label>
                  <select name="subcategory" value={formData.subcategory} onChange={handleInputChange} disabled={!formData.section}>
                    <option value="">Выберите подкатегорию</option>
                    {filteredSubcategories.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="post-edit-form-row">
                <div className="post-edit-form-group">
                  <label className="post-edit-checkbox-label">
                    <input
                      type="checkbox"
                      name="pinned"
                      checked={formData.pinned}
                      onChange={handleInputChange}
                    />
                    Закрепить
                  </label>
                </div>

                <div className="post-edit-form-group">
                  <label className="post-edit-checkbox-label">
                    <input
                      type="checkbox"
                      name="is_public"
                      checked={formData.is_public}
                      onChange={handleInputChange}
                    />
                    Публичный
                  </label>
                </div>

                <div className="post-edit-form-group">
                  <label className="post-edit-checkbox-label">
                    <input
                      type="checkbox"
                      name="is_important"
                      checked={formData.is_important}
                      onChange={handleInputChange}
                    />
                    Важно
                  </label>
                </div>
              </div>

              <div className="post-edit-form-group">
                <label>Дата и время публикации</label>
                <input
                  type="datetime-local"
                  name="created_at"
                  value={formData.created_at}
                  onChange={handleInputChange}
                  className="post-edit-datetime-input"
                />
                <p style={{ marginTop: '4px', fontSize: '12px', color: 'var(--eu-text-muted)' }}>
                  Измените дату и время, чтобы изменить время публикации поста
                </p>
              </div>

              <div className="post-edit-form-group">
                <label className="post-edit-checkbox-label">
                  <input
                    type="checkbox"
                    name="hasPoll"
                    checked={pollData.hasPoll}
                    onChange={handlePollChange}
                  />
                  Добавить опрос
                </label>
              </div>

              {pollData.hasPoll && (
                <div className="post-edit-poll-form">
                  <div className="post-edit-form-group">
                    <label>Вопрос опроса *</label>
                    <input
                      type="text"
                      name="question"
                      value={pollData.question}
                      onChange={handlePollChange}
                      placeholder="Введите вопрос"
                      required={pollData.hasPoll}
                    />
                  </div>

                  <div className="post-edit-form-group">
                    <label className="post-edit-checkbox-label">
                      <input
                        type="checkbox"
                        name="multiple"
                        checked={pollData.multiple}
                        onChange={handlePollChange}
                      />
                      Разрешить множественный выбор
                    </label>
                  </div>

                  <div className="post-edit-form-group">
                    <label>Варианты ответов *</label>
                    {pollData.options.map((option, idx) => (
                      <div key={idx} className="post-edit-poll-option">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handlePollOptionChange(idx, e.target.value)}
                          placeholder={`Вариант ${idx + 1}`}
                          required={pollData.hasPoll}
                        />
                        {pollData.options.length > 2 && (
                          <button
                            type="button"
                            className="post-edit-remove-option"
                            onClick={() => removePollOption(idx)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="post-edit-add-option"
                      onClick={addPollOption}
                    >
                      + Добавить вариант
                    </button>
                  </div>
                </div>
              )}

              <div className="post-edit-form-group">
                <label>Новые файлы (можно добавить несколько)</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    id="file-upload-post-edit"
                    className="file-upload-input"
                    multiple
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-upload-post-edit" className="file-upload-button">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <span>Выбрать файлы</span>
                  </label>
                </div>
                {/* Существующие файлы */}
                {existingAttachments.length > 0 && (
                  <div className="file-upload-list" style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px', fontWeight: 600, color: 'var(--eu-text)' }}>Существующие файлы:</div>
                    {existingAttachments.map((attachment) => (
                      <div key={attachment.id} className="file-upload-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <div className="file-upload-item-name">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                              <polyline points="13 2 13 9 20 9"></polyline>
                            </svg>
                            <span>{attachment.file_name || 'Файл'}</span>
                          </div>
                          <button
                            type="button"
                            className="file-upload-item-remove"
                            onClick={() => removeExistingAttachment(attachment.id)}
                          >
                            Удалить
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder="Отображаемое название (необязательно)"
                          value={attachmentDisplayNames[attachment.id] || ''}
                          onChange={(e) => updateAttachmentDisplayName(attachment.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid var(--eu-border)',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {/* Новые файлы */}
                {attachments.length > 0 && (
                  <div className="file-upload-list">
                    <div style={{ marginBottom: '8px', fontWeight: 600, color: 'var(--eu-text)' }}>Новые файлы:</div>
                    {attachments.map((file, idx) => (
                      <div key={idx} className="file-upload-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <div className="file-upload-item-name">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                              <polyline points="13 2 13 9 20 9"></polyline>
                            </svg>
                            <span>{file.name}</span>
                          </div>
                          <button
                            type="button"
                            className="file-upload-item-remove"
                            onClick={() => removeAttachment(idx)}
                          >
                            Удалить
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder="Отображаемое название (необязательно)"
                          value={attachmentDisplayNames[`new_${idx}`] || ''}
                          onChange={(e) => updateAttachmentDisplayName(`new_${idx}`, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid var(--eu-border)',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="post-edit-form-actions">
                <button
                  type="button"
                  className="post-edit-cancel"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="post-edit-save"
                  disabled={loading}
                >
                  {loading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </article>
  );
}

